import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getQuery } from '../../helpers/query';
import type QueryFilter from '../../helpers/query-filter';
import type { RootState } from '../../reducers';
import { getQueryFilter } from '../../selectors/search';
import { requestStatus } from '../../shared/statuses';
import { type CombinedFacet, getFacetList } from '../../utils/reselectors';
import SearchSubscription from '../subscriptions/search-subscription';
import FilterBarItem from './filter-bar-item';
import FilterBarSlider from './filter-bar-slider';
import SaveSubscriptionButton from './save-subscription-button';

interface State {
  visibleScrollSize: boolean;
  queryFilter: QueryFilter | null;
  subscribe: boolean;
}

interface FilterBarProps {
  onRequestFoldout: () => void;
  onRequestFilter: (facet: CombinedFacet) => void;
  onRequestSearch: (queryFilter: QueryFilter) => void;
}

const FilterBar = ({ onRequestFoldout, onRequestFilter }: FilterBarProps) => {
  const { t } = useTranslation('search');
  const initSubscribe = getQuery('subscribe');
  const isSearching = useSelector<RootState, boolean>(
    (s) => s.search.requestStatus === requestStatus.FETCHING_LIST,
  );
  const facets = useSelector(getFacetList);
  const queryFilter = useSelector(getQueryFilter);

  const [state, setState] = useState<State>({
    visibleScrollSize: false,
    queryFilter: null,
    subscribe: initSubscribe === 'true',
  });

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isSearching) {
      setState({ ...state, queryFilter });
    }
  }, [isSearching]);

  useEffect(() => {
    ref.current?.addEventListener('scroll', onScroll);

    return () => {
      ref.current?.removeEventListener('scroll', onScroll);
    };
  }, []);

  const onScroll = () => {
    const offset = ref.current?.scrollLeft || 0;
    const nextState = { ...state };
    let hasChanged = false;

    if (offset > 0 && !nextState.visibleScrollSize) {
      nextState.visibleScrollSize = true;
      hasChanged = true;
    } else if (offset < 1 && nextState.visibleScrollSize) {
      nextState.visibleScrollSize = false;
      hasChanged = true;
    }

    if (hasChanged) {
      setState(nextState);
    }
  };

  const onDisplayFacet = (facet: CombinedFacet) => onRequestFilter(facet);
  const onOpenFoldout = () => onRequestFoldout();
  const onToggleSubscription = () => setState({ ...state, subscribe: !state.subscribe });

  const checkboxFilters = useMemo(() => {
    const checkboxes = facets
      .filter((facet) => !facet.hidden)
      .map((facet) => (
        <FilterBarItem key={facet.id} facet={facet} onClick={() => onDisplayFacet(facet)} />
      ));
    return checkboxes;
  }, [facets, queryFilter, onDisplayFacet]);

  return (
    <div data-wx-actiongroupcontainer="">
      {state.subscribe && (
        <SearchSubscription onClose={onToggleSubscription} query={queryFilter.toString()} />
      )}
      <div className="action-group-container-alt" ref={ref}>
        <div className="action-group-container-alt-btn">
          <button
            type="button"
            data-wx-button="light thin full-width"
            title={t('Show all filters', { ns: 'search' })}
            onClick={onOpenFoldout}
          >
            <div className="button-icon">
              <i className="icon-filter no-margin" />
            </div>
            <div className="button-text">{t('All filters', { ns: 'search' })}</div>
          </button>
        </div>
        <SaveSubscriptionButton
          className="action-group-container-alt-btn hidden-md"
          testId="save-search-button-mobile"
        />
      </div>
      <FilterBarSlider>{checkboxFilters}</FilterBarSlider>
    </div>
  );
};

export default FilterBar;
